import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatMoney,
  type InvoiceJobsResponse,
  type InvoiceMatch,
  type InvoiceMatchesResponse,
  type InvoiceMatchRequest,
  type JobResponse,
  type Money,
} from '@k-management/shared';
import { useState } from 'react';
import { Link } from 'react-router';
import { useCurrentUser } from '../../auth/AuthProvider.js';
import { DecimalInput } from '../../components/DecimalInput.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { useSessionValue } from '../../lib/preferences.js';
import { moneyOf } from '../people/people-ui.js';
import { ClientPicker } from '../quotes/ClientPicker.js';
import { CreditNoteMark } from '../treasury/treasury-ui.js';
import { AllocationsDialog } from './InvoiceJobsCard.js';
import { jobKeys, jobPath } from './jobs-ui.js';

/** A rata or a cost an invoice may go to, with the subjects it waits for. */
export interface MatchTarget {
  id: string;
  label: string;
  /** What it still waits for. */
  open: Money;
  subjectIds: string[];
}

const OTHER = 'altro';
const ALL = '';

/**
 * «Fatture da abbinare» (2.56, 29/10/2026), in place of the list of invoices
 * not yet given: of the clients of the commessa, or of the suppliers of its
 * costs, those that fit what it still waits for, with the rata or cost
 * proposed. «Abbina» gives the invoice here; «Non è di questa» takes it away
 * from this commessa only. An invoice all given never comes back.
 */
export function InvoiceMatches({
  job,
  direction,
  targets,
}: {
  job: JobResponse;
  direction: 'emessa' | 'ricevuta';
  targets: MatchTarget[];
}) {
  const user = useCurrentUser();
  const queryClient = useQueryClient();
  const issued = direction === 'emessa';
  const [subject, setSubject] = useSessionValue<string>(
    `job.matches.${direction}.${job.id}.${user.email}`,
    ALL,
  );
  const [other, setOther] = useState<string | null>(null);
  const [scope, setScope] = useSessionValue<'probabili' | 'tutte'>(
    `job.matches.scope.${direction}.${user.email}`,
    'probabili',
  );
  // The rata or cost and the amount chosen on each row, until «Abbina».
  const [chosen, setChosen] = useState<Record<string, { itemId: string; amount: string }>>({});
  const [lines, setLines] = useState<string | null>(null);
  const subjectId = subject === OTHER ? other : subject || null;
  const params = new URLSearchParams({ direction, scope });
  if (subjectId) params.set('subjectId', subjectId);
  const found = useQuery({
    queryKey: [...jobKeys.all, 'invoice-matches', job.id, direction, subjectId, scope],
    queryFn: () =>
      apiRequest<InvoiceMatchesResponse>(`/jobs/${job.id}/invoice-matches?${params.toString()}`),
    enabled: subject !== OTHER || Boolean(other),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: jobKeys.all });
  const match = useMutation({
    mutationFn: (body: InvoiceMatchRequest) =>
      apiRequest<void>(`/jobs/${job.id}/invoice-matches`, { method: 'POST', body }),
    onSuccess: refresh,
  });
  const dismiss = useMutation({
    mutationFn: ({ invoiceId, dismissed }: { invoiceId: string; dismissed: boolean }) =>
      apiRequest<void>(`/jobs/${job.id}/invoice-matches/${invoiceId}/dismissal`, {
        method: 'PUT',
        body: { direction, dismissed },
      }),
    onSuccess: refresh,
  });

  const subjects = found.data?.subjects ?? [];
  const probable = subjects.reduce((total, item) => total + item.probable, 0);
  const noun = issued ? 'cliente' : 'fornitore';
  const itemNoun = issued ? 'rata' : 'costo';
  const rowOf = (item: InvoiceMatch) =>
    chosen[item.invoiceId] ?? { itemId: item.proposedId ?? '', amount: item.free };
  const optionsOf = (item: InvoiceMatch) =>
    targets.filter(
      (target) =>
        (item.subjectId !== null && target.subjectIds.includes(item.subjectId)) ||
        target.id === item.proposedId,
    );
  const problemOf = (item: InvoiceMatch): string | null => {
    const amount = moneyOf(rowOf(item).amount);
    if (amount === null || Number(amount) === 0) return 'Scrivi l’importo';
    if (Math.abs(Number(amount)) > Math.abs(Number(item.free)) + 0.001) {
      return `Restano ${formatMoney(item.free)} da imputare`;
    }
    return null;
  };

  return (
    <div className="card stack">
      <div className="row between">
        <h3>Fatture da abbinare {probable > 0 && <span className="pill warn">{probable}</span>}</h3>
        <div className="row">
          <label className="row small">
            <span className="muted">{issued ? 'Cliente' : 'Fornitore'}</span>
            <select
              value={subject}
              onChange={(event) => {
                setSubject(event.target.value);
                setChosen({});
              }}
            >
              <option value={ALL}>
                {issued ? 'Tutti i clienti della commessa' : 'Tutti i fornitori dei costi'} (
                {probable})
              </option>
              {subjects.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name} ({item.probable})
                </option>
              ))}
              <option value={OTHER}>Altro {noun}…</option>
            </select>
          </label>
          <div className="segmented-small" role="group" aria-label="Quali fatture">
            {(['probabili', 'tutte'] as const).map((item) => (
              <button
                key={item}
                type="button"
                className={scope === item ? 'selected' : ''}
                onClick={() => setScope(item)}
              >
                {item === 'probabili'
                  ? 'Probabili per questa commessa'
                  : `Tutte quelle del ${noun}`}
              </button>
            ))}
          </div>
        </div>
      </div>
      {subject === OTHER && (
        <ClientPicker
          value={other}
          onChange={setOther}
          label={issued ? 'Altro cliente' : 'Altro fornitore'}
          role={issued ? 'cliente' : 'fornitore'}
          returnTo={jobPath(job)}
        />
      )}
      <small className="muted">
        {scope === 'probabili'
          ? `Le fatture già in parte su questa commessa e quelle che stanno in ciò che ${issued ? 'le rate' : 'i costi'} del ${noun} aspettano ancora, dai 30 giorni prima della commessa, con ${issued ? 'la rata' : 'il costo'} più vicino già proposto.`
          : `Tutte le fatture del ${noun} non ancora imputate del tutto, anche quelle tolte da questa commessa.`}{' '}
        Non compaiono mai quelle imputate del tutto e quelle «Non riguarda commesse».
      </small>
      {found.isError && <p className="error">{found.error.message}</p>}
      {found.data &&
        (found.data.invoices.length === 0 ? (
          <p className="muted">Nessuna fattura da abbinare.</p>
        ) : (
          <div className="table-scroll">
            <table className="lines">
              <thead>
                <tr>
                  <th>Fattura</th>
                  <th>{issued ? 'Cliente' : 'Fornitore'}</th>
                  <th>Data</th>
                  <th className="right">Imponibile</th>
                  <th className="right">Da imputare</th>
                  <th className="nowrap">Già su</th>
                  <th>{issued ? 'Rata' : 'Costo'}</th>
                  <th className="right">Importo</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {found.data.invoices.map((item) => {
                  const row = rowOf(item);
                  const problem = problemOf(item);
                  const byLine = item.lines > 1;
                  return (
                    <tr key={item.invoiceId}>
                      <td className="nowrap">
                        <Link to={`/tesoreria/fatture/${item.invoiceId}`}>
                          <CreditNoteMark kind={item.kind} />
                          {item.number}
                        </Link>
                        {byLine && <small className="muted block">{item.lines} righe</small>}
                        {item.dismissed && (
                          <small className="muted block">tolta da questa commessa</small>
                        )}
                      </td>
                      <td>{item.subject}</td>
                      <td className="nowrap">{formatDate(item.date)}</td>
                      <td className="right nowrap">{formatMoney(item.taxable)}</td>
                      <td className="right nowrap">{formatMoney(item.free)}</td>
                      <td className="muted small">
                        {Number(item.here) !== 0 && <div>qui {formatMoney(item.here)}</div>}
                        {item.elsewhere.map((part) => (
                          <div key={part.code}>
                            {part.code} {formatMoney(part.amount)}
                          </div>
                        ))}
                        {Number(item.here) === 0 && item.elsewhere.length === 0 && '—'}
                      </td>
                      <td>
                        {!byLine && (
                          <select
                            className="match-select"
                            value={row.itemId}
                            aria-label={`${itemNoun} per la fattura ${item.number}`}
                            onChange={(event) =>
                              setChosen({
                                ...chosen,
                                [item.invoiceId]: { ...row, itemId: event.target.value },
                              })
                            }
                          >
                            <option value="">— senza {itemNoun}</option>
                            {optionsOf(item).map((target) => (
                              <option key={target.id} value={target.id}>
                                {target.label} · {formatMoney(target.open)} da{' '}
                                {issued ? 'fatturare' : 'consuntivare'}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className="right">
                        {!byLine && (
                          <DecimalInput
                            value={row.amount}
                            onChange={(amount) =>
                              setChosen({ ...chosen, [item.invoiceId]: { ...row, amount } })
                            }
                            decimals={[2, 2]}
                            aria-label={`Importo della fattura ${item.number}`}
                          />
                        )}
                        {!byLine && problem && <small className="error block">{problem}</small>}
                      </td>
                      <td className="nowrap">
                        {byLine ? (
                          <button
                            type="button"
                            className="small primary"
                            title="La fattura ha più righe: ogni riga va al suo costo o alla sua rata"
                            onClick={() => setLines(item.invoiceId)}
                          >
                            Abbina le righe…
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="small primary"
                            disabled={Boolean(problem) || match.isPending}
                            onClick={() =>
                              match.mutate({
                                direction,
                                invoiceId: item.invoiceId,
                                itemId: row.itemId || null,
                                amount: moneyOf(row.amount)!,
                              })
                            }
                          >
                            Abbina
                          </button>
                        )}{' '}
                        <button
                          type="button"
                          className="small"
                          disabled={dismiss.isPending}
                          title={
                            item.dismissed
                              ? 'Torna tra le fatture da abbinare di questa commessa'
                              : 'Non la propongo più su questa commessa; resta da abbinare sulle altre'
                          }
                          onClick={() =>
                            dismiss.mutate({
                              invoiceId: item.invoiceId,
                              dismissed: !item.dismissed,
                            })
                          }
                        >
                          {item.dismissed ? 'Rimetti' : 'Non è di questa'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      {match.isError && <p className="error">{match.error.message}</p>}
      {dismiss.isError && <p className="error">{dismiss.error.message}</p>}
      {lines && <LinesDialog invoiceId={lines} jobId={job.id} onClose={() => setLines(null)} />}
    </div>
  );
}

/** The division by line of an invoice with more lines, as on the invoice. */
function LinesDialog({
  invoiceId,
  jobId,
  onClose,
}: {
  invoiceId: string;
  jobId: string;
  onClose: () => void;
}) {
  const division = useQuery({
    queryKey: [...jobKeys.all, 'invoice', invoiceId],
    queryFn: () => apiRequest<InvoiceJobsResponse>(`/jobs/invoices/${invoiceId}`),
  });
  if (division.isError) return <p className="error">{division.error.message}</p>;
  if (!division.data) return null;
  return (
    <AllocationsDialog invoiceId={invoiceId} data={division.data} jobId={jobId} onClose={onClose} />
  );
}
